import { _isEmpty, _isNil, _isNotBlank, _notNil, uuid } from '@/littledash';
import type { DosingPlanCreateV1, DosingSessionStudyGroupV1 } from '@/model/Dose.model';
import type { GroupTarget, RegisteredAnimalTarget } from '@/model/SamplePlan.model';
import type { TreatmentApiId, TreatmentV1 } from '@/model/Treatment.model';
import type { GroupApiId } from '@/model/TreatmentGroup.model';

/**
 * Row key for animals belonging to no study group. Matches the `unassigned` sentinel the sample
 * plan table already uses so the two screens name the same meta group the same way.
 */
export const UNGROUPED_ROW_KEY = 'unassigned';
export type UngroupedRowKey = typeof UNGROUPED_ROW_KEY;
export type DosingMatrixRowKey = GroupApiId | UngroupedRowKey;

export const UNGROUPED_ROW_LABEL = 'Ungrouped';

export interface DosingMatrixRow {
  key: DosingMatrixRowKey;
  name: string;
  animalCount: number;
  /** Absent on the ungrouped row, which has no study group behind it. */
  group?: GroupTarget;
}

/**
 * A treatment as offered by the grid's picker. `value` doubles as the stored cell value because
 * `MultiSelectEditor` renders single-select cells from the raw value rather than the label, so it
 * has to stay human readable and unique within one row's option set.
 */
export interface TreatmentOption {
  value: string;
  label: string;
  isDisabled?: boolean;
  treatmentApiId?: TreatmentApiId;
}

export interface DosingSessionDraft {
  id: string;
  name: string;
  isCalculationBase: boolean;
  /** Chosen treatment per matrix row. A row left unassigned is simply absent. */
  treatments: Map<DosingMatrixRowKey, TreatmentApiId>;
}

export const createSessionId = (): string => `dosing-session-${uuid()}`;

/**
 * Matrix rows for the study groups the selected animals belong to, in group order, followed by the
 * ungrouped row when any selected animal has no study group.
 *
 * Every row describes animals on the sheet, so a row that holds none of them is omitted — groups
 * and the ungrouped row alike. A consequence worth knowing: treatments mapped to no study group are
 * only reachable while the sheet includes an ungrouped animal.
 */
export const buildDosingMatrixRows = (
  groups: Array<GroupTarget>,
  registeredAnimals: Array<RegisteredAnimalTarget>
): Array<DosingMatrixRow> => {
  const animalCounts = registeredAnimals.reduce<Map<DosingMatrixRowKey, number>>((counts, animal) => {
    const key = (animal.study_group_api_id ?? UNGROUPED_ROW_KEY) as DosingMatrixRowKey;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    return counts;
  }, new Map());

  const groupRows = groups.reduce<Array<DosingMatrixRow>>((rows, group) => {
    const animalCount = animalCounts.get(group.api_id as DosingMatrixRowKey) ?? 0;
    if (animalCount > 0) {
      rows.push({ key: group.api_id as GroupApiId, name: group.name, animalCount, group });
    }
    return rows;
  }, []);

  const ungroupedCount = animalCounts.get(UNGROUPED_ROW_KEY) ?? 0;
  if (ungroupedCount > 0) {
    groupRows.push({ key: UNGROUPED_ROW_KEY, name: UNGROUPED_ROW_LABEL, animalCount: ungroupedCount });
  }

  return groupRows;
};

/**
 * Appends a numeric suffix to any label already taken, so every option in a row can be identified
 * by its displayed value. Treatment labels are not unique within a study.
 */
const uniqueOptionValue = (label: string, taken: Set<string>): string => {
  if (!taken.has(label)) {
    return label;
  }
  let suffix = 2;
  while (taken.has(`${label} (${suffix})`)) {
    suffix += 1;
  }
  return `${label} (${suffix})`;
};

/**
 * Handsontable stores the option `value`, so it carries the treatment label — every treatment type
 * is offered selectable, so the label stays the treatment's own.
 */
const toOption = (treatment: TreatmentV1, taken: Set<string>): TreatmentOption => {
  const treatmentLabel = _isNotBlank(treatment.treatment_label) ? treatment.treatment_label : treatment.api_id;
  const value = uniqueOptionValue(treatmentLabel, taken);
  taken.add(value);
  return {
    value,
    label: value,
    treatmentApiId: treatment.api_id,
  };
};

/**
 * Picker options per matrix row, derived from the `study_groups` each treatment is mapped to.
 *
 * The ungrouped row is offered the complement — treatments mapped to no group at all — so the two
 * kinds of row together partition the study's treatments. A row with nothing mapped to it gets an
 * empty list: leaving a group undosed is done by clearing its cell, not by picking an option.
 */
export const buildRowTreatmentOptions = (
  rows: Array<DosingMatrixRow>,
  treatments: Array<TreatmentV1>
): Map<DosingMatrixRowKey, Array<TreatmentOption>> => {
  const byGroup = treatments.reduce<Map<GroupApiId, Array<TreatmentV1>>>((acc, treatment) => {
    (treatment.study_groups ?? []).forEach(({ api_id: groupApiId }) => {
      const existing = acc.get(groupApiId);
      if (_notNil(existing)) {
        existing.push(treatment);
      } else {
        acc.set(groupApiId, [treatment]);
      }
    });
    return acc;
  }, new Map());

  const ungrouped = treatments.filter((treatment) => _isEmpty(treatment.study_groups));

  return rows.reduce<Map<DosingMatrixRowKey, Array<TreatmentOption>>>((acc, row) => {
    const rowTreatments =
      row.key === UNGROUPED_ROW_KEY ? ungrouped : (byGroup.get(row.key as GroupApiId) ?? ([] as Array<TreatmentV1>));
    const taken = new Set<string>();
    acc.set(
      row.key,
      rowTreatments.map((treatment) => toOption(treatment, taken))
    );
    return acc;
  }, new Map());
};

/** Whether a treatment can actually be assigned to a row. */
export const isSelectable = (option: TreatmentOption): boolean =>
  option.isDisabled !== true && _notNil(option.treatmentApiId);

/**
 * The treatment a row defaults to: the first of its selectable options in the order the picker lists
 * them, so a row offered a choice still starts somewhere rather than blank.
 *
 * The comparator matches `MultiSelectEditor`'s own option ordering — a numeric-aware locale compare on
 * the label — because a default that is not the first thing a designer sees in the list reads as
 * arbitrary.
 */
export const defaultSelectableOption = (options: Array<TreatmentOption>): TreatmentOption | undefined =>
  options
    .filter(isSelectable)
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))[0];

/**
 * Whether a sheet can carry a dosing plan at all, and so whether the wizard's treatment step is
 * worth showing.
 *
 * A session exists to date the sample collections calculated from it, so a sheet whose definitions
 * never calculate collection time has nothing to base on a dose. And a sheet whose rows have no
 * treatment they can be given has nothing to dose them with — a matrix of empty pickers.
 *
 * @param hasCalculateCollectionTime whether any selected sample definition calculates collection time
 * @param rowOptions picker options per matrix row, from `buildRowTreatmentOptions`
 */
export const isDosingPlanApplicable = (
  hasCalculateCollectionTime: boolean,
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>
): boolean =>
  hasCalculateCollectionTime &&
  Array.from(rowOptions.values()).some((options) => options.some((option) => isSelectable(option)));

/**
 * Treatment shown in a matrix cell: the option label when one is chosen, blank otherwise.
 * A treatment that has since been unmapped from the group falls back to its api id so the cell
 * never silently blanks a choice the designer made.
 */
export const sessionCellLabel = (
  session: DosingSessionDraft,
  row: DosingMatrixRow,
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>
): string => {
  const treatmentApiId = session.treatments.get(row.key);
  if (!_notNil(treatmentApiId)) {
    return '';
  }
  const option = (rowOptions.get(row.key) ?? []).find((candidate) => candidate.treatmentApiId === treatmentApiId);
  return option?.value ?? treatmentApiId;
};

/**
 * The `dosing_plan` field of the sample-sheet create payload, or `undefined` when the designer
 * authored no sessions — a sampling sheet without dosing omits the field entirely.
 *
 * @param sessions sessions in display order; the array position becomes each session's 1-based `ordinal`
 * @param clashWindow warn-only window as an ISO-8601 duration, or `null` when no window is set
 */
export const buildDosingPlanPayload = (
  sessions: Array<DosingSessionDraft>,
  clashWindow: string | null
): DosingPlanCreateV1 | undefined => {
  if (_isEmpty(sessions)) {
    return undefined;
  }

  return {
    ...(_isNotBlank(clashWindow) ? { clash_window: clashWindow } : {}),
    sessions: sessions.map((session, index) => ({
      name: session.name.trim(),
      ordinal: index + 1,
      is_calculation_base: session.isCalculationBase,
      treatments: Array.from(session.treatments.entries()).map<DosingSessionStudyGroupV1>(
        ([rowKey, treatmentApiId]) => ({
          study_group_api_id: rowKey === UNGROUPED_ROW_KEY ? null : (rowKey as GroupApiId),
          treatment_api_id: treatmentApiId,
        })
      ),
    })),
  };
};

/**
 * Whether a session name can be saved: non-blank, and not already used by another session.
 * The uniqueness check is case-insensitive to match the server's `lower(name)` unique index.
 */
export const sessionNameError = (
  name: string,
  sessions: Array<DosingSessionDraft>,
  editingSessionId?: string
): string | undefined => {
  const trimmed = name.trim();
  if (!_isNotBlank(trimmed)) {
    return 'Session name is required';
  }
  const clash = sessions.some(
    (session) => session.id !== editingSessionId && session.name.trim().toLowerCase() === trimmed.toLowerCase()
  );
  return clash ? 'A session with this name already exists' : undefined;
};

export const defaultSessionTreatments = (
  rows: Array<DosingMatrixRow>,
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>
): Map<DosingMatrixRowKey, TreatmentApiId> =>
  rows.reduce<Map<DosingMatrixRowKey, TreatmentApiId>>((treatments, row) => {
    const treatmentApiId = defaultSelectableOption(rowOptions.get(row.key) ?? [])?.treatmentApiId;
    if (_notNil(treatmentApiId)) {
      treatments.set(row.key, treatmentApiId);
    }
    return treatments;
  }, new Map());

/** Default name for a new session, numbered past whatever is already there. */
export const nextSessionName = (sessions: Array<DosingSessionDraft>): string => {
  let candidate = sessions.length + 1;
  const taken = new Set(sessions.map((session) => session.name.trim().toLowerCase()));
  while (taken.has(`session ${candidate}`)) {
    candidate += 1;
  }
  return `Session ${candidate}`;
};

export const createInitialSessions = (
  rows: Array<DosingMatrixRow>,
  rowOptions: Map<DosingMatrixRowKey, Array<TreatmentOption>>
): Array<DosingSessionDraft> => {
  const treatments = defaultSessionTreatments(rows, rowOptions);
  if (treatments.size === 0) {
    return [];
  }

  return [
    {
      id: createSessionId(),
      name: nextSessionName([]),
      isCalculationBase: true,
      treatments,
    },
  ];
};

/**
 * Moves a session one place left or right, returning a new array. Out-of-range moves are no-ops so
 * the caller does not have to guard the ends of the list.
 */
export const reorderSessions = (
  sessions: Array<DosingSessionDraft>,
  sessionId: string,
  direction: -1 | 1
): Array<DosingSessionDraft> => {
  const index = sessions.findIndex((session) => session.id === sessionId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= sessions.length) {
    return sessions;
  }
  const reordered = [...sessions];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
  return reordered;
};

/**
 * Applies a saved session, replacing the one with the same id or appending it, and keeps
 * `is_calculation_base` a single choice across the plan.
 */
export const upsertSession = (
  sessions: Array<DosingSessionDraft>,
  session: DosingSessionDraft
): Array<DosingSessionDraft> => {
  const exists = sessions.some((existing) => existing.id === session.id);
  const next = exists
    ? sessions.map((existing) => (existing.id === session.id ? session : existing))
    : [...sessions, session];
  if (!session.isCalculationBase) {
    return next;
  }
  return next.map((existing) => ({ ...existing, isCalculationBase: existing.id === session.id }));
};

/** Marks one session as the calculation base and clears the flag on every other session. */
export const setCalculationBase = (sessions: Array<DosingSessionDraft>, sessionId: string): Array<DosingSessionDraft> =>
  sessions.map((session) => ({ ...session, isCalculationBase: session.id === sessionId }));

export const removeSession = (
  sessions: Array<DosingSessionDraft>,
  sessionId: string
): { sessions: Array<DosingSessionDraft>; promotedBase?: DosingSessionDraft } => {
  const removed = sessions.find((session) => session.id === sessionId);
  if (_isNil(removed)) {
    return { sessions };
  }

  const remaining = sessions.filter((session) => session.id !== sessionId);
  const promotedBase = remaining[remaining.length - 1];
  if (!removed.isCalculationBase || _isNil(promotedBase)) {
    return { sessions: remaining };
  }

  return { sessions: setCalculationBase(remaining, promotedBase.id), promotedBase };
};

/** Study groups the given assignments leave without a treatment, in matrix row order. */
export const blankRowNames = (
  rows: Array<DosingMatrixRow>,
  treatments: Map<DosingMatrixRowKey, TreatmentApiId>
): Array<string> =>
  rows.reduce<Array<string>>((names, row) => {
    if (_isNil(treatments.get(row.key))) {
      names.push(row.name);
    }
    return names;
  }, []);

export const blankCalculationBaseWarning = (sessionName: string, blankRows: Array<string>): string =>
  `No treatment is set for ${blankRows.join(', ')} in "${sessionName}". Sample collection times are calculated from that session, so planned collection times will not be populated for those animals.`;

export const calculationBaseBlanksWarning = (
  sessions: Array<DosingSessionDraft>,
  rows: Array<DosingMatrixRow>
): string | undefined => {
  const bases = sessions.filter((session) => session.isCalculationBase);
  if (bases.length !== 1) {
    return undefined;
  }
  const [base] = bases;
  const blankRows = blankRowNames(rows, base.treatments);
  return _isEmpty(blankRows) ? undefined : blankCalculationBaseWarning(base.name, blankRows);
};

/**
 * Whether the plan can be submitted. A plan with sessions must nominate exactly one calculation
 * base; a plan with no sessions at all is always valid.
 */
export const dosingPlanValidationError = (sessions: Array<DosingSessionDraft>): string | undefined => {
  if (_isEmpty(sessions)) {
    return undefined;
  }
  const baseCount = sessions.filter((session) => session.isCalculationBase).length;
  if (baseCount !== 1) {
    return 'Choose exactly one session to base dose calculations on';
  }
  return undefined;
};
